"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";

import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { getConnection } from "@/frontend/lib/connections";
import { useSidebarStore } from "@/frontend/lib/stores";

export function ConnectDialog() {
  const router = useRouter();
  const { connectDialogOption, setConnectDialogOption } = useSidebarStore();

  const currentConnection = connectDialogOption
    ? getConnection(connectDialogOption)
    : undefined;

  if (!currentConnection) return null;

  return (
    <Dialog
      open={!!connectDialogOption}
      onOpenChange={(open) => {
        if (!open) setConnectDialogOption(null);
      }}
    >
      <DialogContent className="sm:max-w-sm" elevated>
        <DialogTitle className="hidden">
          Connect to {currentConnection.name}
        </DialogTitle>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="flex gap-3">
            <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-background text-primary">
              <Image src="/logo.svg" alt="Scout Logo" width={32} height={32} />
            </div>
            <div className="h-16 content-center pt-2">
              <svg
                width="34"
                height="6"
                viewBox="0 0 34 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M31.6152 5.28613C31.458 5.51294 31.1469 5.56916 30.9199 5.41211C30.6933 5.25489 30.636 4.94371 30.793 4.7168L31.6152 5.28613ZM17.749 0.0273438C18.7769 0.00976683 19.7722 -0.0340361 20.9033 0.0742188C21.4499 0.150262 21.9001 0.150305 22.6768 0.500977C22.9278 0.619455 23.1321 0.904724 23.1875 1.08594C23.252 1.27331 23.2536 1.3899 23.2559 1.48047C23.2546 1.65521 23.2327 1.74664 23.2168 1.83105C23.1827 1.99291 23.149 2.10355 23.1182 2.21191C23.0958 2.2886 23.0746 2.36155 23.0547 2.43164C23.0628 2.43613 23.0716 2.44073 23.0801 2.44531C23.1125 2.45207 23.1668 2.46239 23.2354 2.46582C23.3878 2.47739 23.5647 2.48338 23.7422 2.48926C24.0959 2.50055 24.4608 2.5107 24.8232 2.52246C26.8 2.58461 28.7827 2.67269 30.7588 2.78125C30.839 2.78547 30.9157 2.78968 30.9941 2.79395C31.2501 2.80732 31.5065 2.822 31.7617 2.83594C31.6735 2.76846 31.5818 2.69918 31.4902 2.62988L30.6406 1.98926L30.5664 1.91992C30.4136 1.74495 30.3989 1.48027 30.5449 1.28809C30.691 1.09602 30.9497 1.03943 31.1592 1.13965L31.2461 1.19238L32.0938 1.83203C32.4125 2.07345 32.7322 2.31921 32.9453 2.49316C33.1448 2.65601 33.3025 2.79619 33.3906 2.92676C33.4393 2.99908 33.5177 3.14273 33.4961 3.33105C33.4753 3.5102 33.3684 3.6271 33.3652 3.63086C33.3159 3.69156 33.241 3.76362 33.1729 3.82715C33.0961 3.89861 32.9974 3.98723 32.8828 4.08887C32.6568 4.28924 32.3503 4.5567 32 4.86523L31.8428 5.0166C31.7144 5.1459 31.6634 5.21654 31.6152 5.28613L31.2041 5.00098L30.793 4.7168C30.9038 4.55669 31.0223 4.40849 31.3193 4.13281L31.3291 4.12402C31.4971 3.976 31.6514 3.8392 31.792 3.71582C31.5298 3.73856 31.2682 3.75984 31.0068 3.78125C30.9281 3.78746 30.851 3.79371 30.7705 3.7998C28.7938 3.95226 26.8138 4.07326 24.8369 4.16797C24.4754 4.18487 24.1142 4.20185 23.7471 4.21582C23.5605 4.22239 23.3774 4.22928 23.168 4.22754C22.9697 4.2174 22.6769 4.24412 22.2627 4.04688C22.1194 3.97709 21.9313 3.89356 21.7031 3.72461C21.589 3.63613 21.4531 3.52568 21.3174 3.31445C21.1803 3.11346 21.0771 2.73356 21.1475 2.42285C21.1682 2.30531 21.192 2.18932 21.2168 2.07812C21.0579 2.05664 20.8908 2.03881 20.7207 2.02539C19.8333 1.96097 18.7963 2.00695 17.7988 2.02637C17.2739 2.03948 16.7534 2.05346 16.2246 2.06738C11.7304 2.19267 7.21635 2.2316 2.69922 2.12598C2.13894 2.11311 1.57728 2.09804 1.0166 2.08105C0.920678 2.07802 0.82653 2.04209 0.755859 1.97559C0.685408 1.90909 0.64288 1.81814 0.638672 1.72266C0.634594 1.6272 0.668993 1.53329 0.733398 1.46094C0.797941 1.38849 0.888127 1.3435 0.983398 1.33203C1.54043 1.26578 2.09823 1.2017 2.65527 1.13965C7.14801 0.641505 11.6418 0.269499 16.1582 0.0800781C16.6908 0.05863 17.2161 0.0406643 17.749 0.0273438Z"
                  fill="white"
                />
              </svg>
            </div>
            <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <currentConnection.Icon className="size-8" />
            </div>
          </div>
          <h3 className="text-lg font-semibold">
            Connect to {currentConnection.name}
          </h3>
          <p className="max-w-80 text-center text-sm text-muted-foreground">
            {currentConnection.description}
          </p>
        </div>
        <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={() => {
              router.push(currentConnection.authRoute);
              setConnectDialogOption(null);
            }}
          >
            Connect
          </Button>
          <Button
            variant="ghost"
            className="bg-muted-foreground/20 hover:bg-muted-foreground/10"
            onClick={() => setConnectDialogOption(null)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
